/**
 * Connector-domain enums shared by every connector-scoped feature (wizard, KG,
 * Data Studio). Transcribed verbatim from the backend OpenAPI spec
 * (`doc-hub` @ `feat/add-data-studio-apis`, `api/openapi.yaml`).
 *
 * These live here rather than under a single feature so the next consumer
 * narrows the legacy `asset_type: string` on
 * `ConnectorAssetResponsePayload` (services/api.ts) instead of declaring a
 * parallel union.
 */

/**
 * `connector_assets.asset_type` — every node type in the connector asset tree,
 * across all connector shapes (relational, BI, SaaS, document, file, event,
 * unstructured).
 */
export type ConnectorAssetType =
  | "relational_db"
  | "relational_schema"
  | "relational_table"
  | "relational_view"
  | "relational_column"
  | "bi_workspace"
  | "bi_project"
  | "bi_workbook"
  | "bi_dashboard"
  | "bi_view"
  | "bi_tile"
  | "bi_field"
  | "saas_object"
  | "saas_field"
  | "doc_base"
  | "doc_table"
  | "doc_page"
  | "doc_property"
  | "file"
  | "sheet"
  | "sheet_column"
  | "event"
  | "event_property"
  | "unstructured_document"
  | "unstructured_section"
  | "unstructured_chunk";

/** Catalog `connector_catalog.key` (must match migration seed keys). */
export type ConnectorType =
  | "tableau"
  | "powerbi"
  | "looker"
  | "metabase"
  | "qlik"
  | "snowflake"
  | "bigquery"
  | "databricks"
  | "sqlserver"
  | "redshift"
  | "postgres"
  | "mysql"
  | "salesforce"
  | "hubspot"
  | "stripe"
  | "shopify"
  | "zendesk"
  | "intercom"
  | "netsuite"
  | "ga4"
  | "mixpanel"
  | "amplitude"
  | "mailchimp"
  | "quickbooks"
  | "workday"
  | "greenhouse"
  | "jira"
  | "asana"
  | "linear"
  | "notion"
  | "airtable"
  | "confluence"
  | "csv"
  | "xlsx"
  | "google_sheets"
  | "slack"
  | "gong"
  | "pdf";

/**
 * The three `asset_type` values the v2 asset endpoints currently accept as
 * "tabular". Endpoints C/E return `400` for anything else, so this is the
 * guard the UI checks before offering a profile view.
 */
export type TabularConnectorAssetType =
  | "relational_table"
  | "relational_view"
  | "sheet";

export const TABULAR_CONNECTOR_ASSET_TYPES: readonly TabularConnectorAssetType[] =
  ["relational_table", "relational_view", "sheet"] as const;

export const isTabularConnectorAssetType = (
  assetType: ConnectorAssetType,
): assetType is TabularConnectorAssetType =>
  (TABULAR_CONNECTOR_ASSET_TYPES as readonly string[]).includes(assetType);
